/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { goatRepository } from '@/services/repositories/goat.repository.js';

/**
 * Goat Seeder
 * Creates dummy goat data for development
 */

function createSampleNote(goatName) {
  return `Initial check for ${goatName}\n${goatName} looks healthy`;
}


const FIRST_NAMES = ['Billy', 'Nanny', 'Buck', 'Daisy', 'Charlie', 'Luna', 'Max', 'Bella', 'Rocky', 'Clover'];
const LAST_NAMES = ['Hopper', 'Jumper', 'Climber', 'Runner', 'Walker', 'Dancer', 'Prancer', 'Skipper'];
const BREEDS = ['Boer', 'Alpine', 'Saanen', 'Nubian', 'LaMancha', 'Toggenburg', 'Oberhasli', 'Nigerian Dwarf'];
const BIRTH_PLACES = ['Mountain Barn', 'Valley Farm', 'Alpine Pasture', 'Home Farm', 'North Ranch', 'South Valley'];

/**
 * Generate a random date within the last 2 years
 */
function randomBirthDate() {
  const now = new Date();
  const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
  const randomTime = twoYearsAgo.getTime() + Math.random() * (now.getTime() - twoYearsAgo.getTime());
  return new Date(randomTime).toISOString().split('T')[0]; // YYYY-MM-DD format
}

/**
 * Generate random goat data
 */
function generateGoat(groupIds, index) {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  const breed = BREEDS[Math.floor(Math.random() * BREEDS.length)];
  const birthPlace = BIRTH_PLACES[Math.floor(Math.random() * BIRTH_PLACES.length)];
  const gender = Math.random() > 0.5 ? 'Male' : 'Female';
  
  // Assign to group (some goats unassigned)
  let groupId = null;
  if (groupIds.length > 0 && Math.random() > 0.2) { // 80% assigned to groups
    groupId = groupIds[Math.floor(Math.random() * groupIds.length)];
  }

  // Some goats may not have names or birth places (optional fields)
  const hasName = Math.random() > 0.2; // 80% have names
  const hasBirthPlace = Math.random() > 0.3; // 70% have birth places
  const hasEartag = Math.random() > 0.15; // 85% have eartags

  // Generate eartag with fixed prefix and random 5-digit suffix
  // Format: DE 0106 123 [5 random digits]
  let eartag = null;
  if (hasEartag) {
    const PREFIX = 'DE 0106 123';
    const suffix = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
    eartag = `${PREFIX} ${suffix}`;
  }

  const name = hasName ? `${firstName} ${lastName}` : null;

  return {
    name: name,
    dateOfBirth: randomBirthDate(),
    placeOfBirth: hasBirthPlace ? birthPlace : null,
    breed: breed,
    gender: gender,
    eartag: eartag, // Now optional - can be null
    groupId: groupId,
    notes: createSampleNote(name ?? 'Unnamed goat')
  };
}

/**
 * Seed goats into the database
 * @param {Array<number>} groupIds - Array of group IDs to assign goats to
 * @param {number} count - Number of goats to create (default: 20)
 * @returns {Promise<Array<number>>} Array of created goat IDs
 */
export async function seedGoats(groupIds = [], count = 20) {
  console.log(`Seeding ${count} goats...`);
  const goatIds = [];

  try {
    for (let i = 0; i < count; i++) {
      const goat = generateGoat(groupIds, i);
      const id = await goatRepository.create(goat);
      goatIds.push(id);
      
      const groupInfo = goat.groupId ? `Group: ${goat.groupId}` : 'Unassigned';
      console.log(`  Created goat: ${goat.name} (${goat.gender}, ${groupInfo})`);
    }

    console.log(`Seeded ${goatIds.length} goats`);
    return goatIds;
  } catch (error) {
    console.error('Failed to seed goats:', error);
    throw error;
  }
}
